import { HTMLAttributes } from 'react'
import Link from 'next/link'

import AudioIcon from '@/components/base/AudioIcon'
import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import LegalsMenu from '@/components/base/LegalsMenu'
import Logo from '@/components/base/Logo'
import SocialMenu from '@/components/base/SocialMenu'
import Typography from '@/components/base/Typography'
import { useMixer, useMixerStore } from '@/contexts/mixer'
import { useStore } from '@/contexts/store'

import { cn } from '@/lib/utils'

export type FooterProps = HTMLAttributes<HTMLDivElement>

const Footer = ({ className, ...props }: FooterProps) => {
  const { menu, strings } = useStore()
  const { toggleSound } = useMixer()
  const muted = useMixerStore((state) => state.muted)

  return (
    <footer className={cn('relative z-footer bg-black-dark', className)} {...props}>
      <Container className="py-spacing-lg">
        <Grid className="gap-y-spacing-lg">
          <div className={cn('col-span-4 md:col-span-3 lg:col-span-6')}>
            <div className="grid gap-y-spacing-sm">
              <Logo />

              <Typography variant="regular" paragraph className="text-text-secondary-dark">
                {strings?.footer.description}
              </Typography>

              <div className="flex items-center space-x-spacing-sm">
                <SocialMenu />

                <AudioIcon
                  muted={muted}
                  className="h-[1.6rem] w-auto touch:hidden [&>svg]:-translate-y-[0.4rem]"
                  data-motion-audio
                  onClick={(event) => {
                    event.stopPropagation()
                    toggleSound()
                  }}
                />
              </div>
            </div>
          </div>

          <div
            className={cn(
              'col-span-4 md:col-span-3 lg:col-span-6',
              'grid grid-cols-4 gap-x-gutter gap-y-spacing-lg md:grid-cols-3 lg:grid-cols-6'
            )}
          >
            <div className="col-span-4 md:col-span-3 lg:col-span-2">
              <div className="grid gap-y-spacing-sm">
                <Typography variant="large" component="h6">
                  {strings?.footer.solutions}
                </Typography>
                <ul className="grid gap-y-spacing-sm">
                  {menu?.solution_menu.menu.map(({ url, target, title }, index) => (
                    <li key={index} className="flex">
                      <Link
                        href={url}
                        target={target}
                        scroll={false}
                        className={cn(
                          'inline-flex text-text-secondary-dark',
                          'transition-colors duration-300 ease-out-cubic',
                          'hover:text-text-primary-dark'
                        )}
                      >
                        <Typography variant="regular">{title}</Typography>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="col-span-4 md:col-span-3 lg:col-span-2">
              <div className="grid gap-y-spacing-sm">
                <Typography variant="large" component="h6">
                  {strings?.footer.resources}
                </Typography>
                <ul className="grid gap-y-spacing-sm">
                  {menu?.resources_menu.menu.map(({ url, target, title }, index) => (
                    <li key={index} className="flex">
                      <Link
                        href={url}
                        target={target}
                        scroll={false}
                        className={cn(
                          'inline-flex text-text-secondary-dark',
                          'transition-colors duration-300 ease-out-cubic',
                          'hover:text-text-primary-dark'
                        )}
                      >
                        <Typography variant="regular">{title}</Typography>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="col-span-4 md:col-span-3 lg:col-span-2">
              <div className="grid gap-y-spacing-sm">
                <Typography variant="large" component="h6">
                  {strings?.footer.links}
                </Typography>
                <ul className="grid gap-y-spacing-sm">
                  {menu?.important_links_menu.menu.map(({ url, target, title }, index) => (
                    <li key={index} className="flex">
                      <Link
                        href={url}
                        target={target}
                        scroll={false}
                        className={cn(
                          'inline-flex text-text-secondary-dark',
                          'transition-colors duration-300 ease-out-cubic',
                          'hover:text-text-primary-dark'
                        )}
                      >
                        <Typography variant="regular">{title}</Typography>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="col-span-4 md:col-span-3 lg:col-span-6">
            <div className="flex">
              <Typography variant="small" className="opacity-50">
                {strings?.copyright.replace('[YEAR]', new Date().getFullYear())}
              </Typography>
            </div>
          </div>

          <div className="col-span-4 md:col-span-3 lg:col-span-6">
            <LegalsMenu />
          </div>
        </Grid>
      </Container>
    </footer>
  )
}

export default Footer
